import bridge from '@vkontakte/vk-bridge';
import { isVkDesktopPlatform } from './launchParams';

type BridgeSend = (method: string, params?: Record<string, unknown>) => Promise<unknown>;

type OpenLinkEvent = {
  preventDefault: () => void;
};

/**
 * Sync open that still runs inside the click gesture tick.
 * Prefer this over `window.open` after an `await` — mobile WebViews block that.
 */
function openViaAnchor(url: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.target = '_blank';
  anchor.rel = 'noopener noreferrer';
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

function openFallback(url: string): void {
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (opened == null) {
    openViaAnchor(url);
  }
}

/**
 * Open an external URL via VK bridge when embedded, otherwise rely on the
 * caller (e.g. `<a target="_blank">`) or `window.open` for programmatic calls.
 *
 * Mobile VK WebView often fails `VKWebAppOpenLink` and blocks `window.open`
 * after `await`. There we keep native `<a>` navigation (no preventDefault) and
 * still fire OpenLink in parallel. Desktop keeps preventDefault + bridge.
 * `VKWebAppOpenLink` is a client method; typings in @vkontakte/vk-bridge omit it.
 */
export async function openExternalLink(
  url: string,
  event?: OpenLinkEvent,
): Promise<void> {
  if (url === '') {
    return;
  }

  if (bridge.isEmbedded()) {
    const sendOpenLink = () =>
      (bridge.send as BridgeSend)('VKWebAppOpenLink', { url });

    // Mobile: do not cancel the user-gesture <a> navigation. Bridge alone is
    // unreliable there; preventDefault + post-await window.open usually no-ops.
    if (!isVkDesktopPlatform()) {
      void sendOpenLink().catch(() => {
        /* native <a> or sync fallback below */
      });
      if (event == null) {
        openViaAnchor(url);
      }
      return;
    }

    event?.preventDefault();
    try {
      await sendOpenLink();
    } catch {
      openFallback(url);
    }
    return;
  }

  // Native <a target="_blank"> already opens the tab when `event` is present.
  if (event == null) {
    openFallback(url);
  }
}
